import IllnessIcon from "modules/common/components/icons/illness.icon.svg?react";
import VacationIcon from "modules/common/components/icons/vacation.icon.svg?react";
import { Constants } from "modules/root/models/constants";
import React from "react";
import { AbsenceValue } from "../models/plan-types";
import WithoutPayIcon from "./without-work.svg?react";

/** Кнопка отсутствия в окне выбора: те же типы, что и в табеле */
export interface PlanAbsenceOption extends AbsenceValue {
    /** Подпись на кнопке */
    label: string;
    icon: React.ReactNode;
    /** Кнопка занимает отдельную строку — в первую все четыре не влезают */
    wide?: boolean;
}

/**
 * Отпуск, больничный, «без сохранения» и отпуск по беременности — ровно те же
 * типы часов, которыми закрывают день в табеле. В плане дни только рабочие, поэтому проверять
 * `availableForDay` не нужно: типов, запрещённых для выходного, здесь не будет.
 */
export function planAbsenceOptions(): PlanAbsenceOption[] {
    const options: PlanAbsenceOption[] = [
        {
            type: Constants.withoutPayHoursType.name,
            shortName: Constants.withoutPayHoursType.shortName,
            label: "БС",
            icon: <WithoutPayIcon className="figma-icon" />,
        },
        {
            type: Constants.vacationHoursType.name,
            shortName: Constants.vacationHoursType.shortName,
            label: "Отпуск",
            icon: <VacationIcon className="figma-icon" />,
        },
        {
            type: Constants.pregnancyHoursType.name,
            shortName: Constants.pregnancyHoursType.shortName,
            label: "По беременности",
            icon: <VacationIcon className="figma-icon" />,
        },
        // четыре кнопки в одну строку не помещаются — больничный уходит на вторую
        {
            type: Constants.illnesHoursType.name,
            shortName: Constants.illnesHoursType.shortName,
            label: "Больничный",
            icon: <IllnessIcon className="figma-icon" />,
            wide: true,
        },
    ];

    // типы приходят с метаданными приложения; пустой означает, что справочник не доехал
    return options.filter((option) => !!option.type);
}

/**
 * На сетке отпуск по беременности подписывается «ОБ», а не табельной буквой «Р»:
 * одна буква рядом с «О» отпуска читается как опечатка
 */
export const PREGNANCY_SHORT_NAME = "ОБ";

/** Подпись отсутствия на блоке: табельное сокращение, кроме беременности */
export function absenceShortName(absence: AbsenceValue): string {
    return absence.type === Constants.pregnancyHoursType.name ? PREGNANCY_SHORT_NAME : absence.shortName;
}

/** Группа отсутствия — от неё зависит цвет блока на сетке */
export type PlanAbsenceKind = "withoutPay" | "vacation" | "illness" | "pregnancy" | "other";

/**
 * Цвет блока задаётся не инлайном, а классом по группе: палитра живёт в
 * planr/variables.scss (красный — БС, синий — отпуск, зелёный — больничный,
 * жёлтый — по беременности, как в табеле). Прочие типы часов (простой, отгул) приезжают из табеля — им остаётся серый.
 */
export function absenceKind(type: string): PlanAbsenceKind {
    if (type && type === Constants.withoutPayHoursType.name) {
        return "withoutPay";
    }

    if (type && type === Constants.vacationHoursType.name) {
        return "vacation";
    }

    if (type && type === Constants.pregnancyHoursType.name) {
        return "pregnancy";
    }

    if (type && type === Constants.illnesHoursType.name) {
        return "illness";
    }

    return "other";
}
